<script setup lang="ts">
import NewsCard from '@/components/News/NewsCard.vue'
import StatisticCard from '@/components/News/StatisticCard.vue'

import type {
  DailyStatisticDTO,
  DailyStatisticItem,
  MainPageNews,
} from '@@/server/types/common'

const { isMobile } = useResponsive()

const { data: newsData } = await useNuxtApi<MainPageNews>('/api/news/list', {
  query: {
    page: 1,
    size: 4,
  },
})

const { data, error } = await useNuxtApi<DailyStatisticDTO>('/api/daily')

const isFetchDailyStatisticsError = ref(Boolean(error.value))

const dailyStatisticsData = ref<DailyStatisticItem[]>([
  {
    value: 0,
    splitted_value: [0],
    unit: '',
    title: '',
  },
  {
    value: 0,
    splitted_value: [0],
    unit: '',
    title: '',
  },
  {
    value: 0,
    splitted_value: [0],
    unit: 'мин',
    title: 'Среднее <br/> время доставки',
  },
  {
    value: 0,
    splitted_value: [0],
    unit: '',
    title: 'Заказов выполнено',
  },
  {
    value: 0,
    splitted_value: [0],
    unit: 'км',
    title: 'Проехали наши курьеры',
  },
  {
    value: 0,
    splitted_value: [0],
    unit: '',
    title: 'Человек <br/> мы накормили',
  },
  {
    value: 0,
    splitted_value: [0],
    unit: '',
    title: '',
  },
  {
    value: 0,
    splitted_value: [0],
    unit: 'млн',
    title: 'Заказов за всё время',
  },
])

dailyStatisticsData.value = data.value?.statistics || []

const dailyStatisticsList = computed(() => {
  const list: DailyStatisticItem[] = []

  for (const [index, statistic] of dailyStatisticsData.value.entries()) {
    const defaultStatistic = { ...statistic }

    switch (index) {
      case 0:
        defaultStatistic.unit = 'городов'
        defaultStatistic.title = ''

        break

      case 1:
        defaultStatistic.unit = 'кухонь'
        defaultStatistic.title = ''
        break

      case 2:
        defaultStatistic.unit = 'минут'
        defaultStatistic.title = 'Среднее время доставки'
        break

      case 3:
        defaultStatistic.unit = 'заказов'
        defaultStatistic.title = 'За сегодня приготовили'
        break

      case 4:
        defaultStatistic.unit = 'км'
        defaultStatistic.title = 'Наши курьеры проехали'
        break

      case 6:
        defaultStatistic.unit = 'сотрудников'
        defaultStatistic.title = 'Для вас стараются'
        break

      case 5:
      case 7:
        continue
    }

    list.push(defaultStatistic)
  }

  return list
})
</script>

<template>
  <div class="news-block">
    <div id="newsBlock" class="news">
      <h2 class="news-block__title">Новости</h2>

      <div class="news-list">
        <div class="news-list__column">
          <nuxt-link
            v-for="news in newsData?.results.slice(0, 3)"
            :key="news.slug"
            class="news-list__link"
            :to="`/news/${news.slug}/`"
          >
            <news-card :news="news" />
          </nuxt-link>
        </div>
        <div class="news-list__column">
          <div v-if="!isMobile" class="news-list__column--2">
            <nuxt-link
              v-for="news in newsData?.results.slice(3, 5)"
              :key="news.slug"
              :to="`/news/${news.slug}/`"
              :title="news.title"
              class="news-list__link"
            >
              <news-card :news="news" />
            </nuxt-link>
          </div>

          <the-button title="Новости" to="/news/" variant="secondary" block>
            Еще новости
          </the-button>
        </div>
      </div>
    </div>

    <div
      v-if="!isFetchDailyStatisticsError"
      id="dailyStatistics"
      class="daily-statistics"
    >
      <h2 class="news-block__title">Цифры дня</h2>
      <div class="daily-statistics__list">
        <statistic-card
          v-for="(statistic, index) in dailyStatisticsList"
          :key="statistic.title"
          class="daily-statistics__statisctic"
          :class="`v-statisctic--${index}`"
          :statistic="statistic"
          :index="index"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.news-block {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0 20px 80px 20px;

  @include media($bp-tablet) {
    flex-direction: column-reverse;
    padding: 0 48px 139px 48px;
  }

  @include media($bp-desktop-mini) {
    flex-direction: row;
    padding: 0 0 140px;
  }

  &__title {
    margin-bottom: 40px;
    font-size: 30px;
    font-weight: 800;
    color: var(--ui-color-white);

    @include media($bp-tablet) {
      font-size: 40px;
    }
  }
}

.news-list {
  display: flex;
  flex-direction: column;
  gap: 17px;
  padding-bottom: 80px;

  @include media($bp-tablet) {
    flex-direction: row;
    padding-bottom: 0;
  }

  @include media($bp-desktop-mini) {
    flex-direction: column;
    max-width: 282px;
  }

  @include media($bp-desktop) {
    flex-direction: row;
    max-width: none;
  }

  &__link {
    height: max-content;
    transition: 0.2s ease-in;

    &:hover {
      cursor: pointer;
      transform: translateY(5px);
    }
  }

  &__column,
  &__column--2 {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__column--2 {
    display: flex;

    @include media($bp-desktop-mini) {
      display: none;
    }

    @include media($bp-desktop) {
      display: flex;
    }
  }
}

.daily-statistics {
  @include media($bp-tablet) {
    padding-bottom: 142px;
  }

  @include media($bp-desktop) {
    padding-bottom: 0;
  }

  &__list {
    position: relative;
    width: calc(100% - 17px);
    height: 720px;
    margin-left: 17px;
    overflow: hidden;

    @include media($bp-tablet) {
      width: 463px;
      height: 532px;
      margin-left: 0;
      overflow: auto;
    }
  }

  &__statisctic {
    position: absolute;
  }

  // городов
  .v-statisctic--0 {
    top: 24px;
    left: 1px;

    @include media($bp-tablet) {
      top: 30px;
      left: 0;
    }
  }

  // кухонь
  .v-statisctic--1 {
    top: 133px;
    right: 64px;

    @include media($bp-tablet) {
      top: 75px;
      right: auto;
      left: 188px;
    }
  }

  // среднее время доставки
  .v-statisctic--2 {
    top: 650px;
    right: 86px;

    @include media($bp-tablet) {
      top: 461px;
      right: auto;
      left: 49px;
    }
  }

  // сегодня приготовили
  .v-statisctic--3 {
    top: 380px;
    right: 30px;

    @include media($bp-tablet) {
      top: 239px;
      right: auto;
      left: 288px;
    }
  }

  // курьеры км проехали
  .v-statisctic--4 {
    top: 270px;
    left: 12px;

    @include media($bp-tablet) {
      top: 256px;
      left: 18px;
    }
  }

  // сотрудников
  .v-statisctic--5 {
    top: 515px;
    left: 1px;

    @include media($bp-tablet) {
      top: 412px;
      left: 278px;
    }
  }
}
</style>
